import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Textarea,
} from '@travellegacy/nexops-design-system';
import { cashBoxAPI } from '@/shared/lib/api';
import { formatCurrency } from '@/shared/lib/utils';
import { useAuth } from '@/shared/contexts/AuthContext';
import {
  AlertTriangle,
  ChevronDown,
  ChevronRight,
  Landmark,
  History,
  Loader2,
  Lock,
  TrendingUp,
  Users,
  Wallet,
} from 'lucide-react';

interface MonthCashBox {
  month: number;
  year: number;
  netProfit: number;
  profitAdvances: number;
  profitAfterAdvances: number;
  cumulativeCash: number;
}

interface PartnerCashBox {
  id: string;
  name: string;
  email: string;
  partnershipPercentage: number;
  proportionalCash: number;
  totalAdvances: number;
  realCash: number;
}

interface CompanyMonthCashBox {
  month: number;
  year: number;
  isClosed: boolean;
  netProfit: number;
  debtPayments: number;
  variation: number;
  cumulativeCash: number;
}

interface CompanyCashBox {
  openingCash: number;
  netProfitSinceCutoff: number;
  debtPaymentsTotal: number;
  currentCash: number;
  initialDebt: number;
  remainingDebt: number;
  monthlyBreakdown: CompanyMonthCashBox[];
}

interface CutoffInfo {
  id: string;
  month: number;
  year: number;
  totalCash: number;
  notes: string | null;
  createdAt: string;
  createdBy: { id: string; name: string } | null;
}

interface CashBoxData {
  totalCash: number;
  monthlyBreakdown: MonthCashBox[];
  partnerBreakdown: PartnerCashBox[];
  cutoff: CutoffInfo | null;
  company: CompanyCashBox | null;
}

interface CutoffPreview {
  month: number;
  year: number;
  closedMonths: number;
  totalCash: number;
  totalDebt: number;
  percentageSum: number;
  partners: {
    employeeId: string;
    name: string;
    partnershipPercentage: number;
    calculatedCash: number;
    initialAmount: number;
  }[];
}

const MONTH_NAMES = [
  'Janeiro',
  'Fevereiro',
  'Março',
  'Abril',
  'Maio',
  'Junho',
  'Julho',
  'Agosto',
  'Setembro',
  'Outubro',
  'Novembro',
  'Dezembro',
];

function valueColor(value: number): string {
  return value < 0 ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400';
}

function getApiErrorMessage(error: unknown, fallback: string): string {
  const data = (error as { response?: { data?: { error?: { message?: string } } } })?.response?.data;
  return data?.error?.message || fallback;
}

/**
 * Caixa por mês e distribuição por sócio no modelo com antecipações
 * (caixa atual sem corte, ou histórico congelado com corte)
 */
function LegacyBreakdown({ data }: { data: CashBoxData }) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Coluna Esquerda - Caixa por Mês */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5" />
            Caixa por Mês
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {data.monthlyBreakdown.length === 0 ? (
              <p className="text-muted-foreground text-center py-8">
                Nenhum mês fechado ainda
              </p>
            ) : (
              data.monthlyBreakdown.map((month) => (
                <div
                  key={`${month.year}-${month.month}`}
                  className="flex items-center justify-between p-4 rounded-lg border bg-card hover:bg-accent/50 transition-colors"
                >
                  <div className="flex flex-col gap-1">
                    <span className="font-medium">
                      {MONTH_NAMES[month.month - 1]} {month.year}
                    </span>
                    <span className="text-sm text-blue-600 dark:text-blue-400">
                      Lucro Líquido: {formatCurrency(month.netProfit)}
                    </span>
                    <span className="text-sm text-orange-600 dark:text-orange-400">
                      Antecipações: -{formatCurrency(month.profitAdvances)}
                    </span>
                    <span className={`text-sm font-medium ${valueColor(month.profitAfterAdvances)}`}>
                      Disponível: {formatCurrency(month.profitAfterAdvances)}
                    </span>
                  </div>
                  <div className="flex flex-col items-end">
                    <span className="text-sm text-muted-foreground">Caixa Acumulado</span>
                    <span className="font-bold text-lg">
                      {formatCurrency(month.cumulativeCash)}
                    </span>
                  </div>
                </div>
              ))
            )}
          </div>
        </CardContent>
      </Card>

      {/* Coluna Direita - Distribuição por Sócio */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Distribuição por Sócio
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {data.partnerBreakdown.length === 0 ? (
              <p className="text-muted-foreground text-center py-8">
                Nenhum sócio cadastrado
              </p>
            ) : (
              data.partnerBreakdown.map((partner) => (
                <div
                  key={partner.id}
                  className="flex items-center justify-between p-4 rounded-lg border bg-card hover:bg-accent/50 transition-colors"
                >
                  <div className="flex flex-col gap-1">
                    <span className="font-medium">{partner.name}</span>
                    <span className="text-xs text-muted-foreground">
                      {partner.partnershipPercentage}% de participação
                    </span>
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    <span className="text-sm text-orange-600 dark:text-orange-400">
                      Antecipações: -{formatCurrency(partner.totalAdvances)}
                    </span>
                    <span className={`font-bold text-lg ${valueColor(partner.realCash)}`}>
                      Caixa Real: {formatCurrency(partner.realCash)}
                    </span>
                  </div>
                </div>
              ))
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

/**
 * Confirmação do corte: mostra os valores que serão congelados
 */
function CutoffDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) {
  const queryClient = useQueryClient();
  const [notes, setNotes] = useState('');

  const { data: preview, isLoading, error: previewError } = useQuery({
    queryKey: ['cash-box-cutoff-preview'],
    queryFn: async () => {
      const response = await cashBoxAPI.getCutoffPreview();
      return response.data.data as CutoffPreview;
    },
    enabled: open,
    staleTime: 0,
  });

  const createCutoff = useMutation({
    mutationFn: (data: { month: number; year: number; notes?: string }) => cashBoxAPI.createCutoff(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cash-box'] });
      queryClient.invalidateQueries({ queryKey: ['partner-debt'] });
      onOpenChange(false);
    },
  });

  const handleConfirm = () => {
    if (!preview) return;
    createCutoff.mutate({ month: preview.month, year: preview.year, notes: notes || undefined });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Congelar caixa</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 px-6">
          {isLoading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-primary" />
            </div>
          ) : previewError || !preview ? (
            <p className="text-sm text-red-600 dark:text-red-400">
              {getApiErrorMessage(previewError, 'Não foi possível calcular a prévia do corte.')}
            </p>
          ) : (
            <>
              <p className="text-sm text-muted-foreground">
                O caixa acumulado até <strong>{MONTH_NAMES[preview.month - 1]} {preview.year}</strong>{' '}
                ({preview.closedMonths} meses fechados) será congelado. A parte de cada sócio vira o saldo
                inicial da dívida da empresa com ele. A partir de{' '}
                {MONTH_NAMES[preview.month % 12]} a página Caixa mostra o caixa da empresa, sem antecipações.
                <strong> Essa ação só pode ser feita uma vez.</strong>
              </p>

              <div className="grid grid-cols-2 gap-4">
                <div className="rounded-lg border p-4">
                  <p className="text-sm text-muted-foreground">Caixa congelado</p>
                  <p className="text-2xl font-bold">{formatCurrency(preview.totalCash)}</p>
                </div>
                <div className="rounded-lg border p-4">
                  <p className="text-sm text-muted-foreground">Dívida inicial com os sócios</p>
                  <p className="text-2xl font-bold">{formatCurrency(preview.totalDebt)}</p>
                </div>
              </div>

              <div className="rounded-lg border divide-y">
                {preview.partners.map((partner) => (
                  <div key={partner.employeeId} className="flex items-center justify-between p-3">
                    <div>
                      <p className="font-medium">{partner.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {partner.partnershipPercentage}% de participação
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold">{formatCurrency(partner.initialAmount)}</p>
                      {partner.calculatedCash < 0 && (
                        <p className="text-xs text-orange-600 dark:text-orange-400">
                          Caixa calculado de {formatCurrency(partner.calculatedCash)}: dívida fica zerada
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {Math.abs(preview.percentageSum - 100) > 0.001 && (
                <p className="flex items-center gap-2 text-sm text-orange-600 dark:text-orange-400">
                  <AlertTriangle className="h-4 w-4" />
                  As participações somam {preview.percentageSum}%, não 100%.
                </p>
              )}

              <Textarea
                id="cutoff-notes"
                label="Observação"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={2}
                placeholder="Opcional"
              />

              {createCutoff.isError && (
                <p className="text-sm text-red-600 dark:text-red-400">
                  {getApiErrorMessage(createCutoff.error, 'Erro ao congelar o caixa.')}
                </p>
              )}
            </>
          )}
        </div>
        <DialogFooter>
          <div className="flex gap-2 pt-4">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button type="button" onClick={handleConfirm} disabled={!preview || createCutoff.isPending}>
              {createCutoff.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Congelando...
                </>
              ) : (
                'Congelar caixa'
              )}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const SUMMARY_CARD_TONES = {
  positive: {
    card: 'border-2 border-green-600 dark:border-green-500 shadow-lg bg-gradient-to-br from-green-50 via-green-50/50 to-transparent dark:from-green-950/30 dark:via-green-950/10',
    value: 'text-green-600 dark:text-green-500',
  },
  negative: {
    card: 'border-2 border-red-600 dark:border-red-500 shadow-lg bg-gradient-to-br from-red-50 via-red-50/50 to-transparent dark:from-red-950/30 dark:via-red-950/10',
    value: 'text-red-600 dark:text-red-500',
  },
};

function SummaryCard({
  title,
  value,
  subtitle,
  icon: Icon,
  tone,
}: {
  title: string;
  value: number;
  subtitle: string;
  icon: React.ElementType;
  tone: keyof typeof SUMMARY_CARD_TONES;
}) {
  const styles = SUMMARY_CARD_TONES[tone];
  // Saldo em caixa negativo fica vermelho mesmo no card verde
  const valueClass = tone === 'positive' ? valueColor(value) : styles.value;

  return (
    <Card className={styles.card}>
      <CardContent className="py-6">
        <div className="flex items-center gap-2 text-muted-foreground">
          <Icon className="h-5 w-5" />
          <span className="text-sm font-medium">{title}</span>
        </div>
        <div className={`mt-2 text-3xl font-bold ${valueClass}`}>
          {formatCurrency(value)}
        </div>
        <p className="mt-1 text-xs text-muted-foreground">{subtitle}</p>
      </CardContent>
    </Card>
  );
}

/**
 * Caixa da empresa depois do corte
 */
function CompanyCashView({ data, cutoff, company }: { data: CashBoxData; cutoff: CutoffInfo; company: CompanyCashBox }) {
  const [showHistory, setShowHistory] = useState(false);
  const cutoffLabel = `${MONTH_NAMES[cutoff.month - 1]} ${cutoff.year}`;

  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <SummaryCard
          title="Saldo em caixa"
          value={company.currentCash}
          subtitle="Caixa congelado + lucro após o corte − pagamentos da dívida"
          icon={Wallet}
          tone="positive"
        />
        <SummaryCard
          title="Dívida com os sócios"
          value={company.remainingDebt}
          subtitle={`Pago ${formatCurrency(company.debtPaymentsTotal)} de ${formatCurrency(company.initialDebt)}`}
          icon={Landmark}
          tone="negative"
        />
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between gap-4">
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5" />
              Caixa da Empresa por Mês
            </CardTitle>
            <Link to="/financial/partner-debt" className="text-sm text-primary hover:underline">
              Ver dívida com os sócios
            </Link>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <div className="flex items-center justify-between p-4 rounded-lg border border-dashed">
              <div className="flex flex-col gap-1">
                <span className="font-medium flex items-center gap-2">
                  <Lock className="h-4 w-4" />
                  Saldo inicial
                </span>
                <span className="text-sm text-muted-foreground">Caixa congelado em {cutoffLabel}</span>
              </div>
              <span className="font-bold text-lg">{formatCurrency(company.openingCash)}</span>
            </div>

            {company.monthlyBreakdown.length === 0 ? (
              <p className="text-muted-foreground text-center py-6">
                Nenhum mês fechado nem pagamento da dívida após o corte
              </p>
            ) : (
              company.monthlyBreakdown.map((month) => (
                <div
                  key={`${month.year}-${month.month}`}
                  className="flex items-center justify-between p-4 rounded-lg border bg-card hover:bg-accent/50 transition-colors"
                >
                  <div className="flex flex-col gap-1">
                    <span className="font-medium">
                      {MONTH_NAMES[month.month - 1]} {month.year}
                      {!month.isClosed && (
                        <span className="ml-2 text-xs font-normal text-muted-foreground">(mês em aberto)</span>
                      )}
                    </span>
                    {month.isClosed ? (
                      <span className="text-sm text-blue-600 dark:text-blue-400">
                        Lucro Líquido: {formatCurrency(month.netProfit)}
                      </span>
                    ) : (
                      <span className="text-sm text-muted-foreground">
                        Lucro entra quando o mês for fechado
                      </span>
                    )}
                    {month.debtPayments > 0 && (
                      <span className="text-sm text-orange-600 dark:text-orange-400">
                        Pagamentos da dívida: -{formatCurrency(month.debtPayments)}
                      </span>
                    )}
                    <span className={`text-sm font-medium ${valueColor(month.variation)}`}>
                      Variação: {formatCurrency(month.variation)}
                    </span>
                  </div>
                  <div className="flex flex-col items-end">
                    <span className="text-sm text-muted-foreground">Saldo em caixa</span>
                    <span className="font-bold text-lg">{formatCurrency(month.cumulativeCash)}</span>
                  </div>
                </div>
              ))
            )}
          </div>
        </CardContent>
      </Card>

      <div className="space-y-4">
        <button
          type="button"
          onClick={() => setShowHistory((value) => !value)}
          className="flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-foreground"
        >
          {showHistory ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          <History className="h-4 w-4" />
          Histórico até {cutoffLabel} (com antecipações, só consulta) · {formatCurrency(data.totalCash)}
        </button>
        {showHistory && <LegacyBreakdown data={data} />}
      </div>
    </>
  );
}

export default function CashBoxPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'ADMIN';
  const [cutoffDialogOpen, setCutoffDialogOpen] = useState(false);

  const { data: cashBoxResponse, isLoading } = useQuery({
    queryKey: ['cash-box'],
    queryFn: async () => {
      const response = await cashBoxAPI.getCashBox();
      return response.data;
    },
    staleTime: 5 * 60 * 1000, // 5 minutos
  });

  const data: CashBoxData = cashBoxResponse?.data || {
    totalCash: 0,
    monthlyBreakdown: [],
    partnerBreakdown: [],
    cutoff: null,
    company: null,
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-96">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  const { cutoff, company } = data;

  return (
    <div className="container space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
            Caixa
          </h1>
          <p className="text-muted-foreground">
            {cutoff
              ? `Caixa da empresa desde o corte de ${MONTH_NAMES[cutoff.month - 1]} ${cutoff.year}`
              : 'Somatório do lucro líquido de todos os meses fechados'}
          </p>
        </div>
        {!cutoff && isAdmin && (
          <Button variant="outline" onClick={() => setCutoffDialogOpen(true)}>
            <Lock className="h-4 w-4 mr-2" />
            Congelar caixa
          </Button>
        )}
      </div>

      {cutoff && company ? (
        <CompanyCashView data={data} cutoff={cutoff} company={company} />
      ) : (
        <>
          {/* Card de Valor Total */}
          <Card className="border-2 border-green-600 dark:border-green-500 shadow-lg bg-gradient-to-br from-green-50 via-green-50/50 to-transparent dark:from-green-950/30 dark:via-green-950/10">
            <CardContent className="py-8">
              <div className="flex items-center justify-between">
                <div className="flex flex-col gap-2">
                  <div className="flex items-center gap-2">
                    <Wallet className="h-7 w-7 text-green-600 dark:text-green-500" />
                    <h2 className="text-2xl font-bold text-card-foreground">
                      Valor Total do Caixa
                    </h2>
                  </div>
                  <p className="text-sm text-muted-foreground pl-9">
                    Baseado em {data.monthlyBreakdown.length} meses fechados
                  </p>
                </div>
                <div className="flex flex-col items-end">
                  <div className="text-5xl font-bold text-green-600 dark:text-green-500">
                    {formatCurrency(data.totalCash)}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          <LegacyBreakdown data={data} />
        </>
      )}

      {isAdmin && <CutoffDialog open={cutoffDialogOpen} onOpenChange={setCutoffDialogOpen} />}
    </div>
  );
}
